import Button, { type ButtonProps } from '@ds/components/actions/Button';
import { KeyboardArrowDownIcon } from '@ds/icons/generated/material';
import React, { forwardRef } from 'react';

import styles from './MenuButton.module.css';

/**
 * `children` is the label. An icon-only trigger has none, so it must be named with `aria-label` or
 * `aria-labelledby` instead: the chevron is decorative, and an unnamed button is announced as just "button".
 */
export type MenuButtonProps = Omit<ButtonProps, 'children'> &
    (
        | { children: Exclude<React.ReactNode, null | undefined | boolean> }
        | ({ children?: never } & ({ 'aria-label': string } | { 'aria-labelledby': string }))
    );

/**
 * The standard trigger for `Menu` — a `variant="text"` `Button` with a trailing
 * chevron that rotates 180° while the menu is open.
 *
 * All the shared trigger chrome (text variant, chevron) lives here so the six-plus
 * table/row/summary menus don't each repeat it. Everything that legitimately varies
 * is a plain `Button` prop passed straight through: `children` (label), `size`,
 * `disabled`, `aria-label` (required for the icon-only variant), etc.
 *
 * The chevron is Button's `endIcon`, so the Button's `size` decides it: 18px at `sm`, 24px at
 * `lg`, the same glyph scale as every other Button. There is deliberately no override.
 *
 * Must be a `forwardRef` that spreads `...rest`: `Menu` clones its `trigger` and
 * merges a ref plus `getReferenceProps()` (the anchor ref, `aria-haspopup` /
 * `aria-expanded`, and open/close handlers) onto it — the chevron rotation reads
 * that `aria-expanded` via CSS.
 *
 * @example
 * <Menu trigger={<Menu.Button>Actions</Menu.Button>}>…</Menu>
 * <Menu trigger={<Menu.Button size="lg">Actions</Menu.Button>}>…</Menu>
 * <Menu trigger={<Menu.Button aria-label="Switch profile" />}>…</Menu>
 */
const MenuButton = forwardRef<HTMLElement, MenuButtonProps>(({ children, ...rest }, ref) => (
    <Button
        ref={ref}
        variant="text"
        endIcon={<KeyboardArrowDownIcon className={styles.chevron} />}
        {...rest}
    >
        {children}
    </Button>
));

MenuButton.displayName = 'Menu.Button';

export default MenuButton;
